import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import {
    collection,
    query,
    where,
    onSnapshot,
    doc,
    getDoc,
    getDocs,
    orderBy,
    Timestamp,
    DocumentData,
    QueryDocumentSnapshot
} from 'firebase/firestore';

// ========================================
// Type Definitions
// ========================================

export interface LocationPoint {
    lat: number;
    lng: number;
}

export interface DeliverySession {
    createdAt: any;
    deliveryDistance: number;
    deliveryEndIndex: number;
    deliveryEndTime: any;
    deliveryStartIndex: number;
    deliveryStartTime: any;
    groupOrderId: string;
    id: string;
    isActive: boolean;
    locationPoints: LocationPoint[];
    sessionEndPoint: LocationPoint;
    sessionEndTime: any;
    sessionStartPoint: LocationPoint;
    sessionStartTime: any;
    totalDistance: number;

    formattedCreatedAt?: string;
    formattedSessionStartTime?: string;
    formattedSessionEndTime?: string;
    formattedDeliveryStartTime?: string;
    formattedDeliveryEndTime?: string;
}

export interface CartInfo {
    cartId: string;
    cartPersonId: string;
    userId: string;
}

export interface RouteStop {
    location: LocationPoint;
    orderId: string;
}

export interface DeliveryRoute {
    distanceInKm: number;
    route: RouteStop[];
    totalDuration: string;
    hubLocation?: LocationPoint;
}

export interface Order {
    orderId: string;
    orderStatus: string;
}

export interface GroupOrder {
    cart: CartInfo;
    createdAt: any;
    deliveryRoute: DeliveryRoute;
    orders: Order[];
    restaurantId: string;
    status: string;
    id?: string;
    formattedCreatedAt?: string;
}

export interface DeliverySessionSummary {
    totalSessions: number;
    totalDeliveryDistance: number;
    totalCalculatedDistance: number;
    totalVariance: number;
    variancePercentage: number;
    activeSessions: number;
    completedSessions: number;
}

export interface GroupedOrdersByCart {
    [cartId: string]: {
        cartId: string;
        cartName?: string;
        groupOrders: GroupOrder[];
        deliverySessions: DeliverySession[];
    };
}

export interface DeliverySessionData {
    deliverySessions: DeliverySession[];
    selectedDeliverySession: DeliverySession | null;
    groupOrders: Map<string, GroupOrder>;
    selectedGroupOrder: GroupOrder | null;
    currentSessionWithGroupOrder: {
        session: DeliverySession | null;
        groupOrder: GroupOrder | null;
    };
    summary: DeliverySessionSummary;
    groupedByCart: GroupedOrdersByCart;
    cartId: string | null;
    cartName: string | null;
    loading: boolean;
    loadingSessions: boolean;
    loadingGroupOrders: boolean;
    loadingSessionDetail: boolean;
    error: Error | null;
    errorSessions: Error | null;
    errorGroupOrders: Error | null;
    selectedDate: string | null;
    selectDeliverySession: (sessionId: string) => Promise<void>;
    clearSelectedSession: () => void;
    refreshData: () => Promise<void>;
    setDateFilter: (date: string | null) => void;
}

interface UseDeliverySessionDataProps {
    cartId?: string | null;
    restaurantId?: string | null;
    sessionId?: string | null;
    groupOrderId?: string | null;
    selectedDate?: string | null;
    skip?: boolean;
    enableRealtime?: boolean;
}

// ========================================
// Utility Functions
// ========================================

const formatDateForFirestore = (date: string): { start: Timestamp; end: Timestamp } => {
    const selectedDateObj = new Date(date);
    selectedDateObj.setHours(0, 0, 0, 0);
    const nextDay = new Date(selectedDateObj);
    nextDay.setDate(nextDay.getDate() + 1);
    return {
        start: Timestamp.fromDate(selectedDateObj),
        end: Timestamp.fromDate(nextDay),
    };
};

const convertGeoPointToLocation = (geopoint: any): LocationPoint => {
    if (!geopoint) return { lat: 0, lng: 0 };
    if (geopoint.latitude !== undefined && geopoint.longitude !== undefined) {
        return { lat: geopoint.latitude, lng: geopoint.longitude };
    }
    return { lat: geopoint.lat || 0, lng: geopoint.lng || 0 };
};

const convertTimestampToString = (timestamp: any): string => {
    if (!timestamp) return '';
    try {
        if (timestamp?.toDate) return timestamp.toDate().toISOString();
        if (timestamp instanceof Date) return timestamp.toISOString();
        if (typeof timestamp === 'string') return timestamp;
    } catch (e) {
        console.warn('Date parsing error:', e);
    }
    return String(timestamp);
};

const calculateSummary = (sessions: DeliverySession[]): DeliverySessionSummary => {
    const totalDeliveryDistance = sessions.reduce((sum, s) => sum + (s.deliveryDistance || 0), 0);
    const totalCalculatedDistance = sessions.reduce((sum, s) => sum + (s.totalDistance || 0), 0);
    const totalVariance = totalCalculatedDistance - totalDeliveryDistance;
    const variancePercentage =
        totalCalculatedDistance > 0 ? (totalVariance / totalCalculatedDistance) * 100 : 0;

    return {
        totalSessions: sessions.length,
        totalDeliveryDistance,
        totalCalculatedDistance,
        totalVariance,
        variancePercentage,
        activeSessions: sessions.filter(s => s.isActive).length,
        completedSessions: sessions.filter(s => !s.isActive).length,
    };
};

const groupSessionsByCart = async (
    sessions: DeliverySession[],
    groupOrdersMap: Map<string, GroupOrder>
): Promise<GroupedOrdersByCart> => {
    const grouped: GroupedOrdersByCart = {};

    for (const session of sessions) {
        const groupOrder = groupOrdersMap.get(session.groupOrderId);
        if (groupOrder?.cart?.cartId) {
            const cartId = groupOrder.cart.cartId;

            if (!grouped[cartId]) {
                grouped[cartId] = {
                    cartId,
                    cartName: '',
                    groupOrders: [],
                    deliverySessions: [],
                };
            }

            const groupOrderExists = grouped[cartId].groupOrders.some(
                g => g.id === session.groupOrderId
            );
            if (!groupOrderExists && groupOrder) {
                grouped[cartId].groupOrders.push(groupOrder);
            }

            grouped[cartId].deliverySessions.push(session);
        }
    }

    return grouped;
};

// ========================================
// Main Hook
// ========================================

export const useDeliverySessionData = ({
    cartId,
    restaurantId,
    sessionId,
    groupOrderId,
    selectedDate,
    skip = false,
    enableRealtime = true,
}: UseDeliverySessionDataProps): DeliverySessionData => {

    // State
    const [deliverySessions, setDeliverySessions] = useState<DeliverySession[]>([]);
    const [selectedDeliverySession, setSelectedDeliverySession] = useState<DeliverySession | null>(null);
    const [loadingSessions, setLoadingSessions] = useState(true);
    const [errorSessions, setErrorSessions] = useState<Error | null>(null);

    const [groupOrders, setGroupOrders] = useState<Map<string, GroupOrder>>(new Map());
    const [selectedGroupOrder, setSelectedGroupOrder] = useState<GroupOrder | null>(null);
    const [loadingGroupOrders, setLoadingGroupOrders] = useState(true);
    const [errorGroupOrders, setErrorGroupOrders] = useState<Error | null>(null);

    const [loadingSessionDetail, setLoadingSessionDetail] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [currentDateFilter, setCurrentDateFilter] = useState<string | null>(selectedDate || null);

    const [currentCartId, setCurrentCartId] = useState<string | null>(cartId || null);
    const [currentCartName, setCurrentCartName] = useState<string | null>(null);
    const [groupedByCart, setGroupedByCart] = useState<GroupedOrdersByCart>({});
    const [summary, setSummary] = useState<DeliverySessionSummary>({
        totalSessions: 0,
        totalDeliveryDistance: 0,
        totalCalculatedDistance: 0,
        totalVariance: 0,
        variancePercentage: 0,
        activeSessions: 0,
        completedSessions: 0,
    });

    // Store unsubscribe functions for cleanup
    const [unsubscribeFunctions, setUnsubscribeFunctions] = useState<Map<string, () => void>>(new Map());

    // ============ Processors ============

    const processDeliverySession = (doc: QueryDocumentSnapshot<DocumentData>): DeliverySession => {
        const data = doc.data();
        const locationPoints = (data.locationPoints || []).map((point: any) =>
            convertGeoPointToLocation(point)
        );

        return {
            id: doc.id,
            createdAt: data.createdAt,
            deliveryDistance: data.deliveryDistance || 0,
            deliveryEndIndex: data.deliveryEndIndex || 0,
            deliveryEndTime: data.deliveryEndTime,
            deliveryStartIndex: data.deliveryStartIndex || 0,
            deliveryStartTime: data.deliveryStartTime,
            groupOrderId: data.groupOrderId || '',
            isActive: data.isActive || false,
            locationPoints,
            sessionEndPoint: convertGeoPointToLocation(data.sessionEndPoint),
            sessionEndTime: data.sessionEndTime,
            sessionStartPoint: convertGeoPointToLocation(data.sessionStartPoint),
            sessionStartTime: data.sessionStartTime,
            totalDistance: data.totalDistance || 0,
            formattedCreatedAt: convertTimestampToString(data.createdAt),
            formattedSessionStartTime: convertTimestampToString(data.sessionStartTime),
            formattedSessionEndTime: convertTimestampToString(data.sessionEndTime),
            formattedDeliveryStartTime: convertTimestampToString(data.deliveryStartTime),
            formattedDeliveryEndTime: convertTimestampToString(data.deliveryEndTime),
        };
    };

    const processGroupOrder = (doc: QueryDocumentSnapshot<DocumentData>): GroupOrder => {
        const data = doc.data();

        const route = (data.deliveryRoute?.route || []).map((stop: any) => ({
            location: convertGeoPointToLocation(stop.location),
            orderId: stop.orderId || '',
        }));

        const hubLocation = data.deliveryRoute?.hubLocation
            ? convertGeoPointToLocation(data.deliveryRoute.hubLocation)
            : undefined;

        return {
            id: doc.id,
            cart: {
                cartId: data.cart?.cartId || '',
                cartPersonId: data.cart?.cartPersonId || '',
                userId: data.cart?.userId || '',
            },
            createdAt: data.createdAt,
            deliveryRoute: {
                distanceInKm: data.deliveryRoute?.distanceInKm || 0,
                route,
                totalDuration: data.deliveryRoute?.totalDuration || '0h 0m',
                hubLocation,
            },
            orders: (data.orders || []).map((order: any) => ({
                orderId: order.orderId || '',
                orderStatus: order.orderStatus || '',
            })),
            restaurantId: data.restaurantId || '',
            status: data.status || '',
            formattedCreatedAt: convertTimestampToString(data.createdAt),
        };
    };

    // ============ Fetch Cart Name ============
    const fetchCartName = useCallback(async (cartIdToFetch: string) => {
        if (!cartIdToFetch) return;
        try {
            const cartDocRef = doc(db, 'Carts', cartIdToFetch);
            const cartDoc = await getDoc(cartDocRef);
            if (cartDoc.exists()) {
                const cartData = cartDoc.data();
                setCurrentCartName(cartData.displayName || cartData.name || cartIdToFetch);
            }
        } catch (err) {
            console.error('Error fetching cart name:', err);
        }
    }, []);

    // ============ EFFECT 1: Fetch Delivery Sessions with Realtime ============
    useEffect(() => {
        if (skip) {
            setLoadingSessions(false);
            return;
        }

        setLoadingSessions(true);
        setErrorSessions(null);

        const sessionsRef = cartId
            ? collection(db, 'Carts', cartId, 'deliverySessions')
            : collection(db, 'deliverySessions');

        let sessionsQuery = query(sessionsRef, orderBy('createdAt', 'desc'));

        if (currentDateFilter) {
            const { start, end } = formatDateForFirestore(currentDateFilter);
            sessionsQuery = query(
                sessionsRef,
                where('createdAt', '>=', start),
                where('createdAt', '<', end),
                orderBy('createdAt', 'desc')
            );
        }

        const handleSnapshot = (snapshot: any) => {
            const sessionList = snapshot.docs.map(processDeliverySession);
            setDeliverySessions(sessionList);
            setSummary(calculateSummary(sessionList));
            setLoadingSessions(false);
        };

        const handleError = (err: Error) => {
            console.error('Error fetching delivery sessions:', err);
            setErrorSessions(err);
            setLoadingSessions(false);
        };

        let unsubscribe: () => void;
        if (enableRealtime) {
            unsubscribe = onSnapshot(sessionsQuery, handleSnapshot, handleError);
        } else {
            getDocs(sessionsQuery).then(handleSnapshot).catch(handleError);
            unsubscribe = () => {};
        }

        return () => {
            if (enableRealtime) unsubscribe();
        };
    }, [cartId, restaurantId, currentDateFilter, skip, enableRealtime]);

    // ============ EFFECT 2: Fetch Group Orders with Realtime Listeners ============
    useEffect(() => {
        if (skip || deliverySessions.length === 0) {
            setLoadingGroupOrders(false);
            return;
        }

        setLoadingGroupOrders(true);
        setErrorGroupOrders(null);

        const groupOrderIds = [...new Set(deliverySessions.map(s => s.groupOrderId))].filter(Boolean);

        if (groupOrderIds.length === 0) {
            setGroupOrders(new Map());
            setLoadingGroupOrders(false);
            return;
        }

        // Clean up previous listeners
        unsubscribeFunctions.forEach((unsubscribe) => unsubscribe());
        unsubscribeFunctions.clear();

        // Set up new real-time listeners for each group order
        groupOrderIds.forEach(groupId => {
            if (!groupId) return;
            
            const groupOrderRef = doc(db, 'GroupedOrders', groupId);
            
            const unsubscribe = onSnapshot(
                groupOrderRef,
                (docSnapshot) => {
                    if (docSnapshot.exists()) {
                        const groupOrder = processGroupOrder(
                            docSnapshot as QueryDocumentSnapshot<DocumentData>
                        );
                        
                        setGroupOrders(prev => {
                            const newMap = new Map(prev);
                            newMap.set(groupId, groupOrder);
                            return newMap;
                        });

                        // Update selected group order if this is the currently selected one
                        setSelectedGroupOrder(current => {
                            if (current?.id === groupId) {
                                return groupOrder;
                            }
                            return current;
                        });
                    } else {
                        // Document was deleted
                        setGroupOrders(prev => {
                            const newMap = new Map(prev);
                            newMap.delete(groupId);
                            return newMap;
                        });
                        
                        setSelectedGroupOrder(current => {
                            if (current?.id === groupId) {
                                return null;
                            }
                            return current;
                        });
                    }
                    setLoadingGroupOrders(false);
                },
                (err) => {
                    console.error(`Error listening to group order ${groupId}:`, err);
                    setErrorGroupOrders(err as Error);
                    setLoadingGroupOrders(false);
                }
            );
            
            setUnsubscribeFunctions(prev => {
                const newMap = new Map(prev);
                newMap.set(groupId, unsubscribe);
                return newMap;
            });
        });

        // Cleanup function
        return () => {
            unsubscribeFunctions.forEach((unsubscribe) => unsubscribe());
            setUnsubscribeFunctions(new Map());
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deliverySessions, skip]);

    // ============ EFFECT 3: Group Sessions by Cart ============
    useEffect(() => {
        if (deliverySessions.length > 0 && groupOrders.size > 0) {
            groupSessionsByCart(deliverySessions, groupOrders)
                .then(setGroupedByCart)
                .catch(err => console.error('Error grouping sessions:', err));
        }
    }, [deliverySessions, groupOrders]);

    // ============ EFFECT 4: Fetch Cart Name ============
    useEffect(() => {
        if (cartId) {
            setCurrentCartId(cartId);
            fetchCartName(cartId);
        }
    }, [cartId, fetchCartName]);

    // ============ EFFECT 5: Load Specific Session with Realtime ============
    useEffect(() => {
        if (!sessionId || skip) return;

        setLoadingSessionDetail(true);

        let unsubscribeSession: (() => void) | undefined;
        let unsubscribeGroupOrder: (() => void) | undefined;

        const setupRealtimeSession = () => {
            const sessionRef = cartId
                ? doc(db, 'Carts', cartId, 'deliverySessions', sessionId)
                : doc(db, 'deliverySessions', sessionId);

            // Listen to session updates
            unsubscribeSession = onSnapshot(
                sessionRef,
                (sessionDoc) => {
                    if (sessionDoc.exists()) {
                        const session = processDeliverySession(
                            sessionDoc as QueryDocumentSnapshot<DocumentData>
                        );
                        setSelectedDeliverySession(session);
                        
                        // If session has groupOrderId, set up listener for it
                        if (session.groupOrderId) {
                            // Clean up previous group order listener
                            if (unsubscribeGroupOrder) {
                                unsubscribeGroupOrder();
                            }
                            
                            const groupOrderRef = doc(db, 'GroupedOrders', session.groupOrderId);
                            unsubscribeGroupOrder = onSnapshot(
                                groupOrderRef,
                                (groupDoc) => {
                                    if (groupDoc.exists()) {
                                        const groupOrder = processGroupOrder(
                                            groupDoc as QueryDocumentSnapshot<DocumentData>
                                        );
                                        setSelectedGroupOrder(groupOrder);
                                        setGroupOrders(prev => {
                                            const newMap = new Map(prev);
                                            newMap.set(session.groupOrderId, groupOrder);
                                            return newMap;
                                        });
                                    }
                                    setLoadingSessionDetail(false);
                                },
                                (err) => {
                                    console.error('Error listening to group order:', err);
                                    setLoadingSessionDetail(false);
                                }
                            );
                        } else {
                            setLoadingSessionDetail(false);
                        }
                    } else {
                        setSelectedDeliverySession(null);
                        setLoadingSessionDetail(false);
                    }
                },
                (err) => {
                    console.error('Error listening to session:', err);
                    setErrorSessions(err as Error);
                    setLoadingSessionDetail(false);
                }
            );
        };

        setupRealtimeSession();

        return () => {
            if (unsubscribeSession) unsubscribeSession();
            if (unsubscribeGroupOrder) unsubscribeGroupOrder();
        };
    }, [sessionId, cartId, skip]);

    // ============ EFFECT 6: Load Specific Group Order with Realtime ============
    useEffect(() => {
        if (!groupOrderId || skip) return;

        const groupOrderRef = doc(db, 'GroupedOrders', groupOrderId);
        
        const unsubscribe = onSnapshot(
            groupOrderRef,
            (docSnapshot) => {
                if (docSnapshot.exists()) {
                    const groupOrder = processGroupOrder(
                        docSnapshot as QueryDocumentSnapshot<DocumentData>
                    );
                    setSelectedGroupOrder(groupOrder);
                    setGroupOrders(prev => {
                        const newMap = new Map(prev);
                        newMap.set(groupOrderId, groupOrder);
                        return newMap;
                    });
                }
            },
            (err) => {
                console.error('Error listening to group order:', err);
                setErrorGroupOrders(err as Error);
            }
        );

        return () => unsubscribe();
    }, [groupOrderId, skip]);

    // ============ EFFECT 7: Overall Loading State ============
    useEffect(() => {
        setLoading(loadingSessions || loadingGroupOrders || loadingSessionDetail);
    }, [loadingSessions, loadingGroupOrders, loadingSessionDetail]);

    // ============ EFFECT 8: Overall Error State ============
    useEffect(() => {
        setError(errorSessions || errorGroupOrders);
    }, [errorSessions, errorGroupOrders]);

    // ============ Actions ============

    const selectDeliverySession = useCallback(
        async (sessionIdToSelect: string) => {
            if (!sessionIdToSelect) return;
            setLoadingSessionDetail(true);

            try {
                // Set up real-time listener for the selected session
                const sessionRef = cartId
                    ? doc(db, 'Carts', cartId, 'deliverySessions', sessionIdToSelect)
                    : doc(db, 'deliverySessions', sessionIdToSelect);

                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const unsubscribe = onSnapshot(
                    sessionRef,
                    (sessionDoc) => {
                        if (sessionDoc.exists()) {
                            const session = processDeliverySession(
                                sessionDoc as QueryDocumentSnapshot<DocumentData>
                            );
                            setSelectedDeliverySession(session);

                            // Set up listener for group order if exists
                            if (session.groupOrderId) {
                                const groupOrderRef = doc(db, 'GroupedOrders', session.groupOrderId);
                                onSnapshot(
                                    groupOrderRef,
                                    (groupDoc) => {
                                        if (groupDoc.exists()) {
                                            const groupOrder = processGroupOrder(
                                                groupDoc as QueryDocumentSnapshot<DocumentData>
                                            );
                                            setSelectedGroupOrder(groupOrder);
                                        }
                                        setLoadingSessionDetail(false);
                                    },
                                    (err) => {
                                        console.error('Error listening to group order:', err);
                                        setLoadingSessionDetail(false);
                                    }
                                );
                            } else {
                                setLoadingSessionDetail(false);
                            }
                        } else {
                            setSelectedDeliverySession(null);
                            setLoadingSessionDetail(false);
                        }
                    },
                    (err) => {
                        console.error('Error listening to session:', err);
                        setLoadingSessionDetail(false);
                    }
                );

                // Store unsubscribe function if needed
                // You might want to manage this in state for cleanup
                
            } catch (err) {
                console.error('Error selecting session:', err);
                setErrorSessions(err as Error);
                setLoadingSessionDetail(false);
            }
        },
        [cartId]
    );

    const clearSelectedSession = useCallback(() => {
        setSelectedDeliverySession(null);
        setSelectedGroupOrder(null);
    }, []);

    const refreshData = useCallback(async () => {
        setLoadingSessions(true);
        setLoadingGroupOrders(true);
        // The real-time listeners will automatically update the data
        setTimeout(() => {
            setLoadingSessions(false);
            setLoadingGroupOrders(false);
        }, 1000);
    }, []);

    const setDateFilter = useCallback((date: string | null) => {
        setCurrentDateFilter(date);
    }, []);

    return {
        deliverySessions,
        selectedDeliverySession,
        groupOrders,
        selectedGroupOrder,
        currentSessionWithGroupOrder: {
            session: selectedDeliverySession,
            groupOrder: selectedGroupOrder,
        },
        summary,
        groupedByCart,
        cartId: currentCartId,
        cartName: currentCartName,
        loading,
        loadingSessions,
        loadingGroupOrders,
        loadingSessionDetail,
        error,
        errorSessions,
        errorGroupOrders,
        selectedDate: currentDateFilter,
        selectDeliverySession,
        clearSelectedSession,
        refreshData,
        setDateFilter,
    };
};

// ========================================
// Specialized Hooks
// ========================================

export const useCartDeliverySessions = (cartId: string, date?: string) =>
    useDeliverySessionData({ cartId, selectedDate: date, enableRealtime: true });

export const useRestaurantDeliverySessions = (restaurantId: string, date?: string) =>
    useDeliverySessionData({ restaurantId, selectedDate: date, enableRealtime: true });

export const useDeliverySessionDetail = (sessionId: string, cartId?: string) =>
    useDeliverySessionData({ sessionId, cartId, enableRealtime: true });

export const useGroupOrderDetail = (groupOrderId: string) => {
    const data = useDeliverySessionData({ groupOrderId, enableRealtime: true });
    return {
        groupOrder: data.selectedGroupOrder,
        loading: data.loadingGroupOrders,
        error: data.errorGroupOrders,
    };
};